import { AxiosResponse } from "axios";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ExportType } from "../../../models/common";
import { Button } from "../../../styles/button";
import { downloadBlob } from "../../../utils/download";
import { promiseToast } from "../../../utils/toasts";
import { FilterExportButtonsContainer } from "./FilterExportButtonsStyles";

export interface IFilterExportAction {
  id: string;
  translationKey: string;
  doExport(): Promise<AxiosResponse<Blob>>;
}

interface IProps {
  doExport?(type: ExportType): Promise<AxiosResponse<Blob>>;
  exportTypes?: ExportType[];
  exportActions?: IFilterExportAction[];
}

const FilterExportButtons: FC<IProps> = ({
  doExport,
  exportTypes = [ExportType.Csv, ExportType.Xlsx],
  exportActions,
}) => {
  const { t } = useTranslation();

  const handleExport = async (
    exportAction: () => Promise<AxiosResponse<Blob>>
  ) => {
    try {
      await promiseToast(
        async () => {
          const response = await exportAction();
          await downloadBlob(response);
        },
        t("pending.export"),
        t("success.export"),
        t("errors.unknown")
      );
    } catch {
      //Noting.
    }
  };

  const handleExportCsv = () => {
    if (doExport) {
      handleExport(() => doExport(ExportType.Csv));
    }
  };

  const handleExportXlsx = () => {
    if (doExport) {
      handleExport(() => doExport(ExportType.Xlsx));
    }
  };

  return (
    <FilterExportButtonsContainer>
      {exportActions
        ? exportActions.map((exportAction) => (
            <Button
              key={exportAction.id}
              ver="secondary"
              onClick={() => handleExport(exportAction.doExport)}
            >
              {t(exportAction.translationKey)}
            </Button>
          ))
        : doExport && (
            <>
              {exportTypes.includes(ExportType.Csv) && (
                <Button ver="secondary" onClick={handleExportCsv}>
                  {t("common.exportCsv")}
                </Button>
              )}
              {exportTypes.includes(ExportType.Xlsx) && (
                <Button ver="secondary" onClick={handleExportXlsx}>
                  {t("common.exportXlsx")}
                </Button>
              )}
            </>
          )}
    </FilterExportButtonsContainer>
  );
};

export default FilterExportButtons;
